// Generic server/client table machinery: sort, search and
// pagination state keyed by table name. The markup stays per-table; this
// is the shared state half.
// Merged onto the web-dashboard prototype (see dashboard.js);
// `this` is the component instance.
export default {

    // Loads one page of a server-paginated table and records the total.
    async fetchList(name, url) {
      const st = this.tableState(name);
      // Stamp each request; a stale response must not clobber a newer one
      // (search keystrokes and page clicks can race).
      const seq = st.seq = (st.seq || 0) + 1;
      const params = new URLSearchParams({ page: st.page, size: st.size });
      if (st.key) {
        params.set('sort', st.key);
        params.set('dir', st.dir > 0 ? 'asc' : 'desc');
      }
      const q = st.q.trim();
      if (q) params.set('q', q);
      if (st.hideClosed) params.set('hide_closed', '1');
      if (st.role && st.role !== 'all') params.set('role', st.role);
      if (st.status && st.status !== 'all') params.set('status', st.status);
      if (st.product) params.set('product', st.product);
      if (st.type && st.type !== 'all') params.set('type', st.type);
      const response = await this.apiFetch(url + (url.includes('?') ? '&' : '?') + params);
      if (!response.ok) {
        // Give feedback instead of leaving a silently blank card. Guard on seq
        // so a stale failure can't toast over a newer successful load. (401s
        // never reach here — apiFetch throws and tears the session down.)
        if (st.seq === seq) this.showToast('Could not load this list. Please try again.', 'danger');
        return false;
      }
      const data = await response.json();
      if (st.seq !== seq) return false;
      this[name] = data.rows || [];
      this._serverTotals[name] = data.total || 0;
      // Rows may vanish from the last page (e.g. after a delete); refetch it.
      const last = this.pageCount(name);
      if (st.page > last) {
        st.page = last;
        return this.fetchList(name, url);
      }
      // Whatever shows rows of this list outside its table (a dialog over one
      // row) follows it here, on the answer actually applied: a caller that
      // awaited its own load may have been overtaken by a newer one, and
      // would otherwise refresh from rows the table no longer holds.
      this.listLoaded?.(name);
      return true;
    },

    _refetch(name) {
      const loader = this._serverTables[name];
      if (loader) this[loader]();
    },

    // The card header's reload button (web-table-card): a fresh page of a
    // server table from the database, the factory grid's reload. A
    // client-mode table holds its whole list already and offers none.
    canReload(name) {
      return !!this._serverTables[name];
    },

    reloadTable(name) {
      if (!this._serverTables[name]) return false;
      this._refetch(name);
      return true;
    },

    tableState(name) {
      let st = this._tableStates[name];
      if (!st) {
        st = this._tableStates[name] = { key: '', dir: 1, page: 1, size: this.rememberedPageSize(name), q: '' };
      }
      return st;
    },

    // Rows per page is a preference, remembered per table across sessions
    // under 'pgsz:<table>' as the factory's grids remember theirs; a table
    // never asked starts at the default.
    rememberedPageSize(name) {
      let saved = NaN;
      try { saved = parseInt(localStorage.getItem('pgsz:' + name), 10); } catch (e) { /* blocked: the default */ }
      return this.pageSizes.includes(saved) ? saved : this.defaultPageSize;
    },

    // A size change keeps the first row on screen in view, as the factory's
    // grids do: page 3 of 25 becomes page 2 of 50, not page 1.
    setPageSize(name, size) {
      const st = this.tableState(name);
      const next = parseInt(size, 10);
      if (!this.pageSizes.includes(next) || next === st.size) return;
      const first = (st.page - 1) * st.size;
      st.size = next;
      st.page = Math.floor(first / next) + 1;
      try { localStorage.setItem('pgsz:' + name, String(next)); } catch (e) { /* full or blocked: the choice lasts the page */ }
      this._refetch(name);
      this._syncUrl();
    },

    // Debounced so server tables aren't refetched on every keystroke. The
    // search runs in the database (or over the full local list for
    // client-mode tables), never just the visible page.
    onSearch(name, value) {
      const st = this.tableState(name);
      if (st.q === value) return;
      st.q = value;
      st.page = 1;
      clearTimeout(this._searchTimers[name]);
      this._searchTimers[name] = setTimeout(() => {
        this._refetch(name);
        this._syncUrl();
        this.update();
        }, 300);
    },

    searchActive(name) {
      return this.tableState(name).q.trim() !== '';
    },

    emptyTitle(name, title) {
      return this.searchActive(name) ? 'No matches' : title;
    },

    emptyDescription(name, description) {
      return this.searchActive(name) ? 'No rows match this search. Try different keywords.' : description;
    },

    sortBy(name, key) {
      const st = this.tableState(name);
      if (st.key === key) {
        st.dir = -st.dir;
      } else {
        st.key = key;
        st.dir = 1;
      }
      st.page = 1;
      this._refetch(name);
      this._syncUrl();
    },

    sortClass(name, key) {
      const st = this.tableState(name);
      if (st.key !== key) return '';
      return st.dir > 0 ? 'asc' : 'desc';
    },

    _cellValue(name, row, key) {
      const derived = this._derivedCells[name + '.' + key];
      return derived ? derived(row) : row[key];
    },

    _compareCells(a, b) {
      if (a == null || a === '') return (b == null || b === '') ? 0 : 1;
      if (b == null || b === '') return -1;
      if (typeof a === 'number' && typeof b === 'number') return a - b;
      if (typeof a === 'boolean' && typeof b === 'boolean') return a - b;
      const sa = String(a);
      const sb = String(b);
      // RFC3339 timestamps compare as dates so mixed UTC offsets order correctly.
      if (/^\d{4}-\d{2}-\d{2}T/.test(sa) && /^\d{4}-\d{2}-\d{2}T/.test(sb)) {
        return new Date(sa) - new Date(sb);
      }
      return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
    },

    // Client-mode tables filter the full local list, mirroring the database
    // search on server tables.
    _filteredRows(name) {
      let rows = this[name] || [];
      const q = this.tableState(name).q.trim().toLowerCase();
      if (q) {
        const keys = this._clientSearchKeys[name] || [];
        rows = rows.filter(row =>
          keys.some(key => String(this._cellValue(name, row, key) ?? '').toLowerCase().includes(q)));
      }
      return rows;
    },

    tableRows(name) {
      // Server-mode tables arrive pre-sorted, pre-filtered and pre-paged.
      if (this._serverTables[name]) {
        return this[name] || [];
      }
      const st = this.tableState(name);
      let rows = this._filteredRows(name);
      if (st.key) {
        rows = rows.slice().sort((a, b) =>
          st.dir * this._compareCells(this._cellValue(name, a, st.key), this._cellValue(name, b, st.key)));
      }
      // Clamp in case rows were removed from the last page.
      const last = this.pageCount(name);
      if (st.page > last) st.page = last;
      const start = (st.page - 1) * st.size;
      return rows.slice(start, start + st.size);
    },

    tableTotal(name) {
      if (this._serverTables[name]) {
        return this._serverTotals[name] || 0;
      }
      return this._filteredRows(name).length;
    },

    pageCount(name) {
      return Math.max(1, Math.ceil(this.tableTotal(name) / this.tableState(name).size));
    },

    // The foot stands under every table that has rows, as the factory's
    // grid foot does: it says how many rows there are even when they fit on
    // one page. Under the empty state there is no table to stand under.
    showPager(name) {
      return this.tableTotal(name) > 0;
    },

    setPage(name, page) {
      const st = this.tableState(name);
      const next = Math.min(Math.max(1, page), this.pageCount(name));
      if (next === st.page) return;
      st.page = next;
      this._refetch(name);
      this._syncUrl();
    },

    firstPage(name) {
      this.setPage(name, 1);
    },

    prevPage(name) {
      this.setPage(name, this.tableState(name).page - 1);
    },

    nextPage(name) {
      this.setPage(name, this.tableState(name).page + 1);
    },

    lastPage(name) {
      this.setPage(name, this.pageCount(name));
    },

    // The page box: whatever was typed, clamped to the pages there are (999
    // lands on the last page); a non-number does nothing.
    jumpToPage(name, value) {
      const page = parseInt(value, 10);
      if (Number.isFinite(page)) this.setPage(name, page);
    },

    onFirstPage(name) {
      return this.tableState(name).page <= 1;
    },

    onLastPage(name) {
      return this.tableState(name).page >= this.pageCount(name);
    },

    // "51–100 / 3,743 rows": WHICH rows are on screen, not just how many
    // exist — the factory foot's words. Tests read the bare total from the
    // foot's data-total.
    pageInfo(name) {
      const total = this.tableTotal(name);
      if (!total) return '0 rows';
      const st = this.tableState(name);
      const start = (st.page - 1) * st.size + 1;
      const end = Math.min(total, st.page * st.size);
      return start.toLocaleString('en-US') + '–' + end.toLocaleString('en-US') + ' / ' + total.toLocaleString('en-US') + ' rows';
    },
};
